import endpoint from 'utils/endpoint';
import { trackEvent } from 'utils/google-tag-manager';
import { useQuery } from '@tanstack/react-query';
import { WalkScoreErrorResponse, WalkScoreResponse } from 'pages/api/walk-score';
import { listingPageModalIds, testIds } from 'constants/test-constants';
import WalkingShoesIcon from 'components/icon/walking-shoes-icon';
import TransitBusIcon from 'components/icon/transit-bus-icon';
import BikeIcon from 'components/icon/bike-icon';
import { buildClassName } from 'utils/build-class-name';
import styles from './style.module.scss';
import { useState } from 'react';

//Walk Score data is cached for 6 hours since it might not be updated frequently
const WALK_SCORE_STALE_TIME = 6 * 60 * 60 * 1000; // 6 hours in milliseconds

export type WalkScoreProps = {
  latitude: number;
  longitude: number;
}

export default function WalkScore({ latitude, longitude }: WalkScoreProps) {
  if (!latitude || !longitude ) {
    return null;
  }

  const [walkScoreData, setWalkScoreData] = useState<WalkScoreResponse | null>(null);

  const { data } = useQuery({
    queryKey: ['WalkScore', latitude, longitude],
    staleTime: WALK_SCORE_STALE_TIME,
    queryFn:  async () => {
      endpoint<Record<string, any>>(`/api/walk-score?latitude=${latitude}&longitude=${longitude}`)
        .then(response => {
          if (response.error) {
            const walkScoreError = response as WalkScoreErrorResponse;
            trackEvent(`walk-score-error-${walkScoreError.errorCode}`, 'Walk Score', walkScoreError?.errorMessage);
            console.error(walkScoreError?.errorMessage);
            return null;
          } else {
            const responseData = response as WalkScoreResponse;
            setWalkScoreData(responseData);
            trackEvent('walk-score-impression', 'Walk Score');
            return responseData;
          }
        })
        .catch(error => {
          console?.error('Failed to fetch walk score: ' + error);
          return null;
        });
    },
  });

  // If the walk score data is not available, don't render the component
  if (!walkScoreData) {
    return null;
  }

  return (
    <div className={styles.component} data-testid={listingPageModalIds.walkScoreComponent}>
      <ScoreChart
        score={walkScoreData?.walkScore}
        description={walkScoreData?.walkScoreDescription}
        walkScoreLink={walkScoreData?.walkScoreLink}
        Icon={WalkingShoesIcon}
        scoreType="Walk"
      />
      <ScoreChart
        score={walkScoreData?.transitScore}
        description={walkScoreData?.transitScoreDescription}
        walkScoreLink={walkScoreData?.walkScoreLink}
        Icon={TransitBusIcon}
        scoreType="Transit"
      />
      <ScoreChart
        score={walkScoreData?.bikeScore}
        description={walkScoreData?.bikeScoreDescription}
        walkScoreLink={walkScoreData?.walkScoreLink}
        Icon={BikeIcon}
        scoreType="Bike"
      />
    </div>
  );
}

type ScoreChartProps = {
  score?: number;
  description?: string;
  Icon: any;
  scoreType: 'Walk' | 'Bike' | 'Transit';
  walkScoreLink?: string;
}

const walkScoreFallbackLink = 'https://www.walkscore.com/score';

const getScoreLabel = (score?: number) => !score ? '-' : score;

const getDescription = (description?: string) => description ? description : 'No Data';

const getCircleColour = (score?: number) => {
  if (!score) {
    return 'grey';
  } else if (score < 40) {
    return 'red';
  } else if (score >= 40 && score < 60) {
    return 'yellow';
  } else {
    return 'green';
  }
};

const ScoreChart = ({ score, description, Icon, scoreType, walkScoreLink = walkScoreFallbackLink }: ScoreChartProps) => {
  return (
    <section>
      <div className={styles.stat}>
        <Icon />
        <a href={walkScoreLink} target="_blank" rel="noopener noreferrer">
          <div className={buildClassName(styles.score, styles[getCircleColour(score)])} data-testid={scoreType + testIds.label}>
            {getScoreLabel(score)}
          </div>
        </a>
      </div>
      <div className={styles.description} data-testid={scoreType + testIds.description}>
        {getDescription(description)}
      </div>
      <sub>
        <a href={walkScoreLink} target="_blank" rel="noopener noreferrer">
          {scoreType} Score®
        </a>
      </sub>
    </section>
  );
};